/**
 * time-off types config (doc 01 A6.7, doc 13 T4). Read `timeoff:read`; mutate
 * `timeoff:manage`. A type's `suppressesAlerts` is the "quiets missing-hours
 * flags" toggle surfaced with a ConsequenceNote. Types mirror `TimeOffTypeView`.
 */
import { get, patch, post } from './transport.js';
/** `GET /timeoff/types` → `{ types }`. */
export async function types(options) {
    const body = await get('/timeoff/types', options);
    return body.types;
}
/** `GET /timeoff/types/:id` → `{ type }`. */
export async function type(id, options) {
    const body = await get(`/timeoff/types/${id}`, options);
    return body.type;
}
/** `POST /timeoff/types` → `{ type }` (needs `timeoff:manage`). */
export async function createType(input, options) {
    const body = await post('/timeoff/types', input, options);
    return body.type;
}
/** `PATCH /timeoff/types/:id` → `{ type }` (needs `timeoff:manage`). */
export async function updateType(id, input, options) {
    const body = await patch(`/timeoff/types/${id}`, input, options);
    return body.type;
}
/**
 * `GET /timeoff/requests` → `{ requests }` (needs `timeoff:read`).
 *
 * The SCOPE is not the filter. The server narrows the register in SQL to the
 * caller and the workers assigned to them, and `workerId` narrows WITHIN that
 * scope and can never widen it — so a worker asking for their own id and a
 * worker asking for nothing get the same rows, and asking for a colleague's id
 * returns an empty list rather than a refusal.
 *
 * `status` is validated as an enum server-side: an unrecognised value is a 400,
 * not a 200 with an empty register, so a typo can no longer be rendered as
 * "you have no pending leave".
 */
export async function requests(query = {}, options) {
    const body = await get('/timeoff/requests', {
        ...options,
        query: { ...query },
    });
    return body.requests;
}
/** `GET /timeoff/requests/:id` → `{ request }` (needs `timeoff:read`). */
export async function request(id, options) {
    const body = await get(`/timeoff/requests/${id}`, options);
    return body.request;
}
/**
 * `POST /timeoff/requests` → `{ request }` (needs `timeoff:request`).
 *
 * FILING FOR SOMEBODY ELSE IS A DIFFERENT ACT. `assertMaySubmitFor` narrows this
 * to the caller's OWN linked worker unless they hold `timeoff:request_all`
 * (org_admin), and the supervisor edge does NOT widen it: filing leave in
 * another person's name is a claim about their schedule and their pay. A 403
 * here is therefore a REFUSAL, never a retryable failure — asking again cannot
 * change whose worker row the caller's login points at.
 *
 * Both instants must carry an offset (`Z` or `±HH:MM`). A bare
 * `<input type="datetime-local">` value does not, and comes back 400.
 */
export async function createRequest(input, options) {
    const body = await post('/timeoff/requests', input, options);
    return body.request;
}
/**
 * `POST /timeoff/requests/:id/cancel` → `{ request }` (needs `timeoff:request`).
 *
 * Withdrawal by the requester. Only `pending` is cancellable — the lifecycle
 * treats a decided or already-cancelled request as terminal — so a 409 here
 * means somebody decided it while the screen was open, and the honest response
 * is to re-read the row rather than to offer the button again.
 *
 * A request outside the caller's scope answers 404, not 403: the existence of a
 * colleague's leave request is itself the disclosure.
 */
export async function cancelRequest(id, options) {
    const body = await post(`/timeoff/requests/${id}/cancel`, undefined, options);
    return body.request;
}
/**
 * `POST /timeoff/requests/:id/decision` → `{ request }` (needs `timeoff:review`).
 *
 * `deny`, NOT `reject` — `decideRequestSchema` is a two-value enum and anything
 * else is a 400 at the boundary.
 *
 * THE KEY IS NOT THE WHOLE ANSWER. `timeoff:review` is seeded to `supervisor`
 * organization-wide and is a DOOR; whom it may be used on is decided next to the
 * row, inside the transaction that locks it, by the same scope the read uses
 * plus separation of duties — nobody decides a request they are the subject of
 * or filed, on EITHER branch. A request outside the reviewer's reach answers the
 * same 404 the read does. Render each of those as a refusal with its own
 * sentence; retrying changes nothing.
 */
export async function decideRequest(id, input, options) {
    const body = await post(`/timeoff/requests/${id}/decision`, input, options);
    return body.request;
}
/**
 * `GET /timeoff/workers/:workerId/on-leave` → `{ workerId, at, onApprovedLeave }`
 * (needs `timeoff:read`).
 *
 * `at` must be ISO 8601 WITH an offset; the route parses it with
 * `z.string().datetime({ offset: true })` and a bare local string is a 400.
 *
 * A 403 HERE IS NOT THE DOOR. `timeoff:read` is seeded to the stock `worker`
 * role organization-wide, so holding it says nothing about whom you may ask
 * about: `refusedUnreachableWorker` refuses per WORKER, on the
 * `worker.supervisor_id` edge, unless the caller holds `timeoff:read_all`. It
 * is a 403 rather than the register's 404 because the worker id was the
 * caller's own input and a truthful answer would not have confirmed anything
 * either way. Render it as "not someone you can ask about", never as "no".
 */
export async function onLeave(workerId, at, options, requestOptions) {
    return get(`/timeoff/workers/${workerId}/on-leave`, {
        ...requestOptions,
        query: { at, suppressingTypesOnly: options.suppressingTypesOnly },
    });
}
/**
 * `GET /timeoff/workers/:workerId/approved-intervals` → `{ intervals }`
 * (needs `timeoff:read`).
 *
 * The window form of the same hook: every APPROVED interval overlapping
 * `[from, to)`. Overlap is half-open on both sides — `startsAt < to AND
 * endsAt > from` — so an interval that merely touches the window's edge is not
 * in it.
 *
 * An EMPTY array means the server looked and found none within the window. It
 * does NOT mean "we could not look": that arrives as a thrown `ForbiddenError`
 * from the per-worker reach check, or as a 400 for a window the route refused.
 * A caller that swallows the throw and renders `[]` has turned a refusal into
 * an assertion that somebody was at work.
 */
export async function approvedIntervals(workerId, window, options, requestOptions) {
    const body = await get(`/timeoff/workers/${workerId}/approved-intervals`, {
        ...requestOptions,
        query: {
            from: window.from,
            to: window.to,
            suppressingTypesOnly: options.suppressingTypesOnly,
        },
    });
    return body.intervals;
}
//# sourceMappingURL=timeoff.js.map